import { MdOutlineRemoveRedEye } from "react-icons/md";
import { BsTrash } from "react-icons/bs";
import { AiOutlineCheck } from "react-icons/ai";
import { FiUser, FiAlertOctagon, FiWifiOff, FiEdit2 } from "react-icons/fi";
import { Link } from "react-router-dom";

interface ItemPaginationProps {
  id: string;
  title: string;
  description?: string;
  subtitle?: string;
  status?: string;
  hasDelete?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  avatarUrl?: string | null;
  zoneColor?: string;
  titleColor?: string;
  hasWarning?: boolean;
  warningMessage?: string;
  hasEmergency?: boolean;
  isEmergency?: boolean;
  onEmergencyClick?: () => void;
  handleChangeStatus?(id: string, newStatus: string): Promise<void>;
}

export function ItemPagination({
  id,
  title,
  description,
  subtitle,
  status,
  hasDelete,
  canEdit = true,
  canDelete = true,
  avatarUrl,
  zoneColor,
  titleColor,
  hasWarning,
  warningMessage,
  hasEmergency,
  isEmergency,
  onEmergencyClick,
  handleChangeStatus,
}: ItemPaginationProps) {
  return (
    <div className="list-item-enter group relative flex items-center justify-between w-full rounded-xl bg-white p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-200">
      {/* Indicador lateral de status */}
      {status && (
        <div
          className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-all duration-200 ${
            status === "A" ? "bg-green-500" : "bg-gray-300"
          }`}
        />
      )}

      <div className="flex items-center gap-4 pl-2">
        {/* Avatar */}
        {avatarUrl !== undefined && (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-white shadow-sm">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : (
              <FiUser size={22} className="text-gray-400" />
            )}
          </div>
        )}

        <div className="flex flex-col gap-1">
          {/* Título com indicadores */}
          <div className="flex items-center gap-2">
            {titleColor && (
              <div
                className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                style={{ backgroundColor: titleColor }}
              />
            )}
            {/* Ícones de alerta */}
            <div className="flex items-center gap-1">
              {isEmergency && (
                <span
                  title="Cliente com emergência ativa"
                  className="animate-pulse"
                >
                  <FiAlertOctagon size={18} className="text-red-500" />
                </span>
              )}
              {hasWarning && (
                <span
                  title={warningMessage || "Endereço incorreto"}
                  className="animate-pulse"
                >
                  <FiWifiOff size={18} className="text-amber-500" />
                </span>
              )}
            </div>
            <h1 className="text-[#0085BD] font-semibold text-base group-hover:text-[#006a96] transition-colors">
              {title}
            </h1>
          </div>

          {/* Descrição com zona */}
          {description !== undefined && (
            <div className="flex items-center gap-2">
              {zoneColor && (
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: zoneColor }}
                />
              )}
              <span className="text-gray-500 text-sm">{description}</span>
            </div>
          )}

          {/* Subtítulo */}
          {subtitle && (
            <div className="text-gray-400 text-xs">{subtitle}</div>
          )}
        </div>
      </div>

      {/* Ações */}
      {hasDelete === true && (
        <div className="flex items-center gap-2">
          {/* Botão de Emergência */}
          {hasEmergency && (
            <button
              type="button"
              onClick={onEmergencyClick}
              className={`group/emergency relative flex items-center gap-2 px-3 py-2 rounded-xl text-white text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ${
                isEmergency
                  ? "bg-gradient-to-r from-red-500 to-red-600 animate-pulse hover:from-red-600 hover:to-red-700"
                  : "bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600"
              }`}
              title={
                isEmergency
                  ? "Cliente com emergência ativa"
                  : "Adicionar observação emergencial"
              }
            >
              <FiAlertOctagon size={14} className="transition-transform duration-200 group-hover/emergency:scale-110" />
              <span className="hidden sm:inline">{isEmergency ? "Emergência" : "Urgente"}</span>
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover/emergency:bg-white/10 transition-colors duration-200" />
            </button>
          )}

          {/* Botão de Status */}
          {canDelete && status && status === "A" ? (
            <button
              type="button"
              onClick={() => handleChangeStatus(id, "I")}
              className="group/status relative flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              title="Desativar"
            >
              <AiOutlineCheck
                size={14}
                className="transition-transform duration-200 group-hover/status:scale-110"
              />
              <span className="hidden sm:inline">Ativo</span>
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover/status:bg-white/10 transition-colors duration-200" />
            </button>
          ) : canDelete && status ? (
            <button
              type="button"
              onClick={() => handleChangeStatus(id, "A")}
              className="group/status relative flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-gray-400 to-gray-500 hover:from-red-500 hover:to-red-600 text-white text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              title="Ativar"
            >
              <BsTrash
                size={14}
                className="transition-transform duration-200 group-hover/status:scale-110"
              />
              <span className="hidden sm:inline">Inativo</span>
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover/status:bg-white/10 transition-colors duration-200" />
            </button>
          ) : null}

          {/* Botão de Editar/Visualizar */}
          {canEdit && (
            <Link
              key={id}
              to={{
                pathname: `edit/${id}`,
              }}
              className="group/edit relative flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-[#0085BD] to-[#006a96] hover:from-[#006a96] hover:to-[#005a80] text-white text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              <FiEdit2
                size={14}
                className="transition-transform duration-200 group-hover/edit:rotate-12"
              />
              <span className="hidden sm:inline">Editar</span>
              {/* Efeito de brilho */}
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover/edit:bg-white/10 transition-colors duration-200" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
